import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Quote, Star } from "lucide-react";

import { COLORS } from "../constants";
import { TESTIMONIALS } from "../data/testimonials";
import SectionHeading from "../components/SectionHeading";
import Button from "../components/Button";

const TestimonialsPage = () => {
  const navigate = useNavigate();

  return (
    <section className="py-24 min-h-screen" style={{ backgroundColor: COLORS.lightSurface }}>
      <Helmet>
        <title>Customer Testimonials | Lectric Leisure</title>
        <meta
          name="description"
          content="Read what golfers and clubs say about Lectric Leisure electric golf scooters."
        />
        <link rel="canonical" href="https://lectricleisure.golf/testimonials" />
      </Helmet>

      <div className="container mx-auto px-6 max-w-6xl">
        <SectionHeading
          title="Stories from the Fairway"
          subtitle="A few words from Lectric Leisure owners and clubs — comfort, stability, and more time playing golf."
        />

        <div className="grid md:grid-cols-3 gap-8 items-stretch">
          {TESTIMONIALS.map((testimonial, idx) => {
            const rating = Math.max(0, Math.min(5, Number(testimonial.rating) || 0));

            return (
              <div
                key={testimonial.id}
                className="bg-white rounded-[2.5rem] shadow-2xl border overflow-hidden h-full"
                style={{ borderColor: "#E0D8C8" }}
              >
                <div
                  className="p-10 h-full flex flex-col"
                  style={{
                    background: `linear-gradient(180deg, ${COLORS.secondaryBg}0D 0%, #FFFFFF 52%)`,
                  }}
                >
                  <div className="flex items-start justify-between gap-6 mb-8">
                    <div className="flex items-center gap-2" aria-label={`${rating} out of 5 stars`}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          size={18}
                          className={i < rating ? "fill-current" : ""}
                          style={{ color: i < rating ? COLORS.accent : "#D6CDBD" }}
                        />
                      ))}
                    </div>

                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: COLORS.secondaryBg }}
                      aria-hidden="true"
                    >
                      <Quote size={22} style={{ color: COLORS.accent }} />
                    </div>
                  </div>

                  <p className="text-lg leading-relaxed mb-10 flex-1" style={{ color: COLORS.textPrimary }}>
                    “{testimonial.text}”
                  </p>

                  <div className="pt-6 border-t" style={{ borderColor: "#EFE8DB" }}>
                    <p className="text-lg font-black" style={{ color: COLORS.secondaryBg }}>
                      {testimonial.name}
                    </p>
                    {testimonial.role && (
                      <p className="text-sm font-bold uppercase tracking-widest opacity-80" style={{ color: "#7A6F60" }}>
                        {testimonial.role}
                      </p>
                    )}

                    {idx === 0 && (
                      <p className="text-sm mt-3 opacity-80" style={{ color: COLORS.textPrimary }}>
                        A smoother round, with less strain — that’s the goal.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div
          className="mt-16 rounded-[3rem] border shadow-2xl p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 bg-white"
          style={{ borderColor: "#E0D8C8" }}
        >
          <div>
            <h3 className="text-3xl md:text-4xl font-serif font-black mb-3" style={{ color: COLORS.secondaryBg }}>
              Want pricing on extras, spares, or upgrades?
            </h3>
            <p className="text-lg opacity-80 max-w-2xl" style={{ color: COLORS.textPrimary }}>
              Tell us what you need and we’ll come back with options and availability.
            </p>
          </div>

          <Button className="w-full md:w-auto" onClick={() => navigate("/quote")}>
            Extras & Spares
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsPage;
